import React, { useState } from 'react';
import { HelpOutline, Close, FileCopyOutlined } from '@material-ui/icons';
import { Formik } from 'formik';
import clsx from 'clsx';
import {
    TextField,
    withStyles,
    Select,
    MenuItem,
    FormControl,
    Button,
    InputLabel,
    Tooltip,
    IconButton,
    FormGroup,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    RadioGroup,
    Radio,
    Input,
    InputAdornment,
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import * as Yup from 'yup';
import JsonMappingHelp from './JsonMappingHelp';
import { defaultJSONMapping } from './constants';
import FormStep2 from './FormStep2';

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    button: {
        width: '120px',
        marginTop: '10px',
        marginRight: '10px'
    },
    formControl: {
        width: '100%'
    }
});

const FormStep1Json = props => {
    const {
        classes,
        formSettings,
        initialValues,
        translations,
        isDirty,
        edit,
        handleSubmit
    } = props;

    const [mappingHelp, setMappingHelp] = useState(false);

    const tooltipText = translations["copy_default_mapping_to_clipboard"] || "Copy default mapping to clipboard.";

    const [copyToClipboardTooltip, setCopyToClipboardTooltip] = useState(tooltipText)

    const [clientSecret, setClientSecret] = React.useState(false);

    const handleClickShowPassword = () => {
        setClientSecret(!clientSecret);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleMappingHelp = () => {
        setMappingHelp(!mappingHelp);
    };

    const handleCopyDefaultMapping = () => {
        navigator.clipboard.writeText(defaultJSONMapping).then(
            response => {
                setCopyToClipboardTooltip(`${translations["copied"] || "Copied"}!`)
                setTimeout(() => {
                    setCopyToClipboardTooltip(tooltipText)
                }, 2000);
            }
        )
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape(formSettings.Validation)}
            onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                await handleSubmit(values);
            }}
            validateOnMount={true}
        >
            {formikProps => {
                const { values, dirty, touched, errors, isValid, isSubmitting, handleChange, handleBlur, handleSubmit } = formikProps;

                return (
                    <div className={classes.root}>
                        <form onSubmit = {handleSubmit}>
                            <>
                                <TextField
                                    name="Name"
                                    value={values.Name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Name && touched.Name)}
                                    placeholder={translations["name"] || "Name"}
                                    label={translations["name"] || "Name"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                                <FormControl id="direction_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations['please_select_direction'] || "Please select direction"}</InputLabel>
                                    <Select
                                        name="Direction"
                                        label={translations['direction'] || 'Direction'}
                                        margin={"dense"}
                                        value={values.Direction}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value={"in"}>
                                            {translations['in'] || 'in'}
                                        </MenuItem>
                                        <MenuItem value={"out"}>
                                            {translations['out'] || 'out'}
                                        </MenuItem>
                                        <MenuItem value={"in+out"}>
                                            {`${translations['in'] || 'in'}+${translations['out'] || 'out'}`}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl id="active_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["active"] || "Active"}</InputLabel>
                                    <Select
                                        name="Active"
                                        value={values.Active}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.Active && touched.Active)}
                                        placeholder={translations["active"] || "Active"}
                                        label={translations["active"] || "Active"}
                                        margin={"dense"}
                                        fullWidth={true}
                                    >
                                        <MenuItem value={true}>
                                            {translations['active'] || 'Active'}
                                        </MenuItem>
                                        <MenuItem value={false}>
                                            {translations['inactive'] || 'Inactive'}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    name="Hostname"
                                    value={values.Hostname}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Hostname && touched.Hostname)}
                                    placeholder={translations["hostname"] || "Hostname"}
                                    label={translations["hostname"] || "Hostname"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                                <TextField
                                    name="Port"
                                    value={values.Port}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    type='number'
                                    error={!!(errors.Port && touched.Port)}
                                    placeholder={translations['port'] || 'Port'}
                                    label={translations['port'] || 'Port'}
                                    margin={'dense'}
                                    fullWidth={true}
                                />
                                <FormControl id="protocol_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations['select_protocol'] || "Please select protocol"}</InputLabel>
                                    <Select
                                        name="Protocol"
                                        label={translations['protocol'] || 'Protocol'}
                                        margin={"dense"}
                                        value={values.Protocol}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value={"http"}>
                                            HTTP
                                        </MenuItem>
                                        <MenuItem value={"https"}>
                                            HTTPS
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl id="auth_type_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations['select_authentication_type'] || "Please select authentication type"}</InputLabel>
                                    <Select
                                        name="AuthType"
                                        label={translations['auth_type'] || 'Auth type'}
                                        margin={"dense"}
                                        value={values.AuthType}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value={"bearer"}>
                                            Bearer
                                        </MenuItem>
                                        <MenuItem value={"none"}>
                                            None
                                        </MenuItem>
                                        <MenuItem value={"basic"}>
                                            Basic
                                        </MenuItem>
                                        <MenuItem value={"api-key"}>
                                            API Key
                                        </MenuItem>
                                        <MenuItem value={"clientid-clientsecret"}>
                                            {translations['clientid_id_and_client_secret_request_headers'] || "Client ID and Client Secret request headers"}
                                            
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                {values.AuthType === 'bearer' ?
                                    <TextField
                                        name="BearerToken"
                                        value={values.BearerToken}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.BearerToken && touched.BearerToken)}
                                        placeholder={translations['bearer_token'] || 'Bearer Token'}
                                        label={translations['bearer_token'] || 'Bearer Token'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    : null}
                                {values.AuthType === 'api-key' ?
                                    <TextField
                                        name="ApiKey"
                                        value={values.ApiKey}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.ApiKey && touched.ApiKey)}
                                        placeholder={translations['api_key'] || 'ApiKey'}
                                        label={translations['api_key'] || 'ApiKey'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    : null}
                                {values.AuthType === "basic" &&
                                    <FormStep2 handleSubmit={handleSubmit} 
                                        initialValues={initialValues} 
                                        translations={translations} 
                                        formSettings={formSettings} 
                                        values={values} 
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        errors={errors}
                                        touched={touched}
                                        isSubmitting={isSubmitting}
                                        isValid={isValid}
                                        dirty={dirty}
                                    />
                                }
                                {values.AuthType === "clientid-clientsecret" &&
                                    <>
                                        <TextField
                                            label={translations['client_id_header_name'] || 'Client ID header name'}
                                            placeholder={translations['client_id_header_name'] || 'Client ID header name'}
                                            name="ClientIDHeaderName"
                                            fullWidth={true}
                                            value={values.ClientIDHeaderName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            margin="dense"
                                            error={!!(errors.ClientIDHeaderName && touched.ClientIDHeaderName)}
                                        />
                                        <TextField
                                            label={translations['client_id_header_value'] || 'Client ID header value'}
                                            placeholder={translations['client_id_header_value'] || 'Client ID header value'}
                                            name="ClientIDHeaderValue"
                                            fullWidth={true}
                                            value={values.ClientIDHeaderValue}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            margin="dense"
                                            error={!!(errors.ClientIDHeaderValue && touched.ClientIDHeaderValue)}
                                        />
                                        <TextField
                                            label={translations['client_secret_header_name'] || 'Client Secret header name'}
                                            placeholder={translations['client_secret_header_name'] || 'Client Secret header name'}
                                            name="ClientSecretHeaderName"
                                            fullWidth={true}
                                            value={values.ClientSecretHeaderName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            margin="dense"
                                            error={!!(errors.ClientSecretHeaderName && touched.ClientSecretHeaderName)}
                                        />
                                        <FormControl className={clsx(classes.margin, classes.formControl)}>
                                            <InputLabel htmlFor="in-category-1">{translations['client_secret_header_value'] || 'Client Secret header value'}</InputLabel>
                                            <Input
                                                id="in-category-1"
                                                type={clientSecret ? 'text' : 'password'}
                                                name="ClientSecretHeaderValue"
                                                value={values.ClientSecretHeaderValue}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={!!(errors.ClientSecretHeaderValue && touched.ClientSecretHeaderValue)}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                        >
                                                            {clientSecret ? <Visibility /> : <VisibilityOff />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>
                                    </>
                                }
                                <TextField
                                    name="Path"
                                    value={values.Path}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Path && touched.Path)}
                                    placeholder={translations['path'] || 'Path'}
                                    label={translations['path'] || 'Path'}
                                    margin={'dense'}
                                    fullWidth={true}
                                />
                                <FormControl id="output_format_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations['please_select_output_format'] || "Please select output format"}</InputLabel>
                                    <Select
                                        name="OutputFormat"
                                        label={translations['output_format'] || 'Output format'}
                                        margin={"dense"}
                                        value={values.OutputFormat}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value={"json"}>
                                            JSON
                                        </MenuItem>
                                        <MenuItem value={"xml"}>
                                            XML
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <br />
                                <br />
                                <InputLabel shrink>{translations['select_external_triggers'] || "Please select triggers for external service"}</InputLabel>
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnSessionStart}
                                                onChange={handleChange}
                                                name="TriggerOnSessionStart"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_start_session'] || 'On start session'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnModifiedWeb}
                                                onChange={handleChange}
                                                name="TriggerOnModifiedWeb"
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_trigger_on_modified_web'] || 'Trigger on WebUI modifications'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnModifiedProcessing}
                                                onChange={handleChange}
                                                name="TriggerOnModifiedProcessing"
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_trigger_on_modified_processing'] || 'Trigger on processing modifications'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnErasureProgress}
                                                onChange={handleChange}
                                                name="TriggerOnErasureProgress"
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_trigger_on_erasure_progress'] || 'Trigger on erasure progress'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnSessionEnd}
                                                onChange={handleChange}
                                                name="TriggerOnSessionEnd"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_end_session'] || 'On end session'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnErasureFinished}
                                                onChange={handleChange}
                                                name="TriggerOnErasureFinished"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_erasure_finished'] || 'On erasure finished'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnAnalyzeFinished}
                                                onChange={handleChange}
                                                name="TriggerOnAnalyzeFinished"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_analyze_finished'] || 'On analyze finished'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnOperatorWorkflowDone}
                                                onChange={handleChange}
                                                name="TriggerOnOperatorWorkflowDone"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_operator_workflow_done'] || 'On operator workflow done'}
                                    />
                                </FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={values.TriggerOnReportGenerated}
                                            onChange={handleChange}
                                            name="TriggerOnReportGenerated"
                                            color="primary"
                                        />
                                    }
                                    label={translations['external_send_erasure_report'] || 'Send erasure report when generated?'}
                                />
                                {
                                    values.TriggerOnReportGenerated ?
                                        <>
                                            <FormGroup row>
                                                <FormControl>
                                                    <RadioGroup
                                                        row
                                                        name={'SendReportMethod'}
                                                        value={values.SendReportMethod}
                                                        onChange={handleChange}
                                                    >
                                                        <FormControlLabel
                                                            value="base64pdf"
                                                            control={<Radio />}
                                                            label={`${translations['send_pdf_report_base64_encoded'] || 'Send pdf report base64 encoded to a separate endpoint'}`}
                                                        />
                                                        <FormControlLabel
                                                            value="downloadLink"
                                                            control={<Radio />}
                                                            label={`${translations['send_report_download_link'] || 'Send download link for report along with other data (table mapping)'}`}
                                                        />
                                                    </RadioGroup>
                                                </FormControl>
                                            </FormGroup>
                                            
                                            <FormGroup row>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={values.SendReportOnSuccessfulErasure}
                                                            onChange={handleChange}
                                                            name="SendReportOnSuccessfulErasure"
                                                            color="primary"
                                                        />
                                                    }
                                                    label={translations['send_report_on_successful_erasure'] || 'Send report on successful erasure'}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={values.SendReportOnFailedErasure}
                                                            onChange={handleChange}
                                                            name="SendReportOnFailedErasure"
                                                            color="primary"
                                                        />
                                                    }
                                                    label={translations['send_report_on_failed_erasure'] || 'Send report on failed erasure'}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={values.SendReportOnNoErasure}
                                                            onChange={handleChange}
                                                            name="SendReportOnNoErasure"
                                                            color="primary"
                                                        />
                                                    }
                                                    label={translations['send_report_on_no_erasure'] || 'Send report on no erasure'}
                                                />
                                            </FormGroup>
                                            {
                                                values.SendReportMethod === 'base64pdf' ?
                                                    <TextField
                                                        name="SendErasureReportEndpoint"
                                                        value={values.SendErasureReportEndpoint}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={!!(errors.SendErasureReportEndpoint && touched.SendErasureReportEndpoint)}
                                                        placeholder={translations["external_send_report_endpoint"] || "Endpoint for sending erasure report."}
                                                        label={translations["external_send_report_endpoint"] || "Endpoint for sending erasure report."}
                                                        margin={"dense"}
                                                        fullWidth={true}
                                                    /> : null
                                            }
                                        </>
                                        : null
                                }
                                <br />
                                <br />
                                <InputLabel shrink>{translations['advanced_customization'] || "Advanced customization"}</InputLabel>
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.SendPerformedDiagnoseCasesOnly}
                                                onChange={handleChange}
                                                name="SendPerformedDiagnoseCasesOnly"
                                                color="primary"
                                            />
                                        }
                                        label={translations['send_performed_diagnose_cases_only'] || 'Send performed diagnose cases only (DiagnoseResults must be included in table mapping)'}
                                    />
                                </FormGroup>
                                <br />
                                <InputLabel shrink>{translations['select_external_behaviour'] || "Please select client behaviours"}</InputLabel>
                                {values.Direction.includes('in') ?
                                    <FormGroup row>
                                        <>
                                            <span style={{ 'vertical-align': 'middle', display: 'inline-flex', 'align-items': 'center', 'margin-right': '16px'}}>{`${translations['external_behaviour_provide_external_id'] || 'Provide External ID on device check for'}: `}</span>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.ProvideExternalIDEnabled.PCProduct}
                                                        onChange={handleChange}
                                                        name="ProvideExternalIDEnabled.PCProduct"
                                                        color="primary"
                                                    />
                                                }
                                                label={translations['pc_product'] || 'Work'}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.ProvideExternalIDEnabled.MobileProduct}
                                                        onChange={handleChange}
                                                        name="ProvideExternalIDEnabled.MobileProduct"
                                                        color="primary"
                                                    />
                                                }
                                                label={translations['mobile_product'] || 'Mobile'}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.ProvideExternalIDEnabled.StorageProduct}
                                                        onChange={handleChange}
                                                        name="ProvideExternalIDEnabled.StorageProduct"
                                                        color="primary"
                                                    />
                                                }
                                                label={translations['storage_product'] || 'Single disk-drives'}
                                            />
                                            {Object.keys(values.ProvideExternalIDEnabled).some(key => values.ProvideExternalIDEnabled[key]) ?
                                                <>
                                                    <TextField
                                                        name="ProvideExternalIDRegexp"
                                                        value={values.ProvideExternalIDRegexp}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={!!(errors.ProvideExternalIDRegexp && touched.ProvideExternalIDRegexp)}
                                                        placeholder={translations["external_id_regexp"] || "External ID Regexp (Leave empty for no regexp check)"}
                                                        label={translations["external_id_regexp"] || "External ID Regexp (Leave empty for no regexp check)"}
                                                        margin={"dense"}
                                                        fullWidth={true}
                                                    />
                                                    <TextField
                                                        name="ProvideExternalIDDescription"
                                                        value={values.ProvideExternalIDDescription}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={!!(errors.ProvideExternalIDDescription && touched.ProvideExternalIDDescription)}
                                                        placeholder={translations["external_id_regexp_description"] || "External ID Regexp Description"}
                                                        label={translations["external_id_regexp_description"] || "External ID Regexp Description"}
                                                        margin={"dense"}
                                                        fullWidth={true}
                                                    />
                                                    <TextField
                                                        name="ProvideExternalIDName"
                                                        value={values.ProvideExternalIDName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={!!(errors.ProvideExternalIDName && touched.ProvideExternalIDName)}
                                                        placeholder={translations["external_id_name"] || "External ID Name on External Service"}
                                                        label={translations["external_id_name"] || "External ID Name on External Service"}
                                                        margin={"dense"}
                                                        fullWidth={true}
                                                    />
                                                </> : null
                                            }
                                        </>
                                    </FormGroup> : null
                                }

                                {values.Direction.includes('out') ?
                                    <FormGroup row>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.ConfirmDataExportEnabled}
                                                    onChange={handleChange}
                                                    name="ConfirmDataExportEnabled"
                                                    color="primary"
                                                />
                                            }
                                            label={translations['external_behaviour_confirm_data_export'] || 'Confirm data export'}
                                        />
                                        {values.ConfirmDataExportEnabled ?
                                            <TextField
                                                name="ConfirmDataExportMessage"
                                                value={values.ConfirmDataExportMessage}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={!!(errors.ConfirmDataExportMessage && touched.ConfirmDataExportMessage)}
                                                placeholder={translations["confirm_data_export_message"] || "Message for data export finished"}
                                                label={translations["confirm_data_export_message"] || "Message for data export finished"}
                                                margin={"dense"}
                                                fullWidth={true}
                                            /> : null
                                        }
                                    </FormGroup>
                                    : null
                                }

                                {mappingHelp ? <Tooltip title={translations["close_mapping_help"] || "Close mapping help screen"}>
                                    <IconButton onClick={handleMappingHelp}>

                                        <Close />
                                    </IconButton>
                                </Tooltip>
                                    :
                                    <Tooltip title={translations["need_help_mapping"] || "Need help with table mapping?"}>
                                        <IconButton onClick={handleMappingHelp}>

                                            <HelpOutline />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {
                                    mappingHelp ?
                                        <JsonMappingHelp />
                                        : null
                                }
                                {
                                    <Tooltip title={copyToClipboardTooltip}>
                                        <IconButton onClick={handleCopyDefaultMapping}>
                                            <FileCopyOutlined />
                                        </IconButton>
                                    </Tooltip>
                                }
                                <TextField
                                    name="TableMapping"
                                    value={values.TableMapping}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    multiline={true}
                                    error={!!(errors.TableMapping && touched.TableMapping)}
                                    placeholder={translations["table_mapping"] || "Table mapping"}
                                    label={translations["table_mapping"] || "Table mapping"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                            </>
                            {formSettings.ButtonSubmit ? (
                                <Button
                                    className={classes.submit}
                                    variant="contained"
                                    color={formSettings.ButtonSubmit.Color}
                                    type="submit"
                                    disabled={!edit ? !isValid || isSubmitting : !((isValid && dirty) || isDirty) || isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                            formSettings.ButtonSubmit.Title
                                        )}
                                </Button>
                            ) : null}
                        </form>
                    </div>
                );
            }}
        </Formik >
    );
};

export default withStyles(styles, { withTheme: true })(FormStep1Json);